import React, { useState } from 'react';
import { Download, Copy, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import { googleDocsExportService } from '../services/googleDocsExportService';

export const GoogleDocsExportDialog = ({ itinerary, onClose }) => {
  const [status, setStatus] = useState(null); // 'success', 'error', 'loading'
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleExportToGoogleDocs = async () => {
    setIsLoading(true);
    setStatus(null);

    try {
      const exportData = await googleDocsExportService.exportToGoogleDoc(itinerary);

      // Copy to clipboard as temporary solution
      const textContent = googleDocsExportService.extractGuideText(itinerary);
      const documentContent = googleDocsExportService.formatAsDocumentContent(textContent);

      await navigator.clipboard.writeText(documentContent);

      setStatus('success');
      setMessage(
        `✅ Content copied to clipboard! (${itinerary.points.length} stops)\n\n` +
        `You can now:\n` +
        `1. Open Google Docs (docs.google.com)\n` +
        `2. Create a new document\n` +
        `3. Paste the content (Ctrl+V)\n` +
        `4. Rename to: "${exportData.title}"`
      );
    } catch (error) {
      setStatus('error');
      setMessage(`Failed to export: ${error.message}`);
      console.error('Google Docs export error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyToClipboard = () => {
    const success = googleDocsExportService.copyToClipboard(itinerary);
    if (success) {
      setStatus('success');
      setMessage(`✅ Copied ${itinerary.points.length} stops to clipboard!`);
    } else {
      setStatus('error');
      setMessage('Failed to copy to clipboard');
    }
  };

  const extractedData = googleDocsExportService.extractGuideText(itinerary);
  const totalWords = Object.values(extractedData.stops || {})
    .reduce((sum, stop) => {
      const content = Object.values(stop).join(' ');
      return sum + content.split(/\s+/).length;
    }, 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-2xl font-bold">Export Guide to Google Docs</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Guide Info */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-900 mb-2">Guide Information</h3>
            <div className="text-sm text-blue-800 space-y-1">
              <p><strong>Guide:</strong> {itinerary.metadata.name}</p>
              <p><strong>Stops:</strong> {itinerary.points.length}</p>
              <p><strong>Total Words (approx):</strong> {totalWords.toLocaleString()}</p>
            </div>
          </div>

          {/* Fields to Export */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-2">Fields Extracted per Stop</h3>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>✓ Stop Name</li>
              <li>✓ Description</li>
              <li>✓ In-Depth Description</li>
              <li>✓ Historical Context</li>
              <li>✓ Cultural Significance</li>
              <li>✓ Hidden Gems</li>
            </ul>
          </div>

          {/* Status Messages */}
          {status === 'success' && (
            <div className="bg-green-50 p-4 rounded-lg border border-green-200 flex gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-green-900">Success!</p>
                <p className="text-sm text-green-800 whitespace-pre-line">{message}</p>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="bg-red-50 p-4 rounded-lg border border-red-200 flex gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-red-900">Error</p>
                <p className="text-sm text-red-800">{message}</p>
              </div>
            </div>
          )}

          {/* Info Message */}
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 text-sm text-yellow-800">
            <p className="font-semibold mb-1">How it works:</p>
            <p>The content will be copied to your clipboard. You can then paste it into a new Google Doc.</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCopyToClipboard}
            disabled={isLoading}
            className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:bg-gray-400 transition-colors flex items-center justify-center gap-2"
          >
            <Copy className="w-4 h-4" />
            Copy to Clipboard
          </button>
          <button
            onClick={handleExportToGoogleDocs}
            disabled={isLoading}
            className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-blue-400 transition-colors flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Export & Copy
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
